import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Card, Table, Spin, Button, Input, notification, Divider, Modal, Select, Avatar, DatePicker, Tabs, Tag, Popconfirm, Form, Upload } from "antd";
import axios from "axios";
import { baseUrl, socketUrl } from "../../config";
import { SendOutlined, FileExcelOutlined, SearchOutlined, UserOutlined, SaveOutlined, CloseOutlined, DownloadOutlined, EditOutlined, EyeOutlined, PlusOutlined, DeleteOutlined, LinkOutlined, UploadOutlined } from "@ant-design/icons";
import userDummy from "../../assets/images/dummy-user.png";
import { hasPermission } from '../../components/permissions';
import '../../assets/styles/custom_style.css';
import moment from 'moment';
import * as XLSX from 'xlsx';

const { Option } = Select;
const { TabPane } = Tabs;

const createStyledTitle = (text) => (
  <span style={{ fontSize: '13px', color: '#7a0000', fontWeight: 700 }}>{text}</span>
);

function BirthdayReport() {
  // ── Registered Members state ───────────────────────────────────────────────
  const [userList, setUserList] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ name: '', phone: '' });

  // ── Active tab ─────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('registered');

  // ── External Contacts state ────────────────────────────────────────────────
  const [extList, setExtList] = useState([]);
  const [extPagination, setExtPagination] = useState({});
  const [extPage, setExtPage] = useState(1);
  const [extLimit, setExtLimit] = useState(20);
  const [extLoading, setExtLoading] = useState(false);
  const [extFilters, setExtFilters] = useState({ name: '', phone: '' });

  // ── Add Contact modal ──────────────────────────────────────────────────────
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addForm] = Form.useForm();
  const [addLoading, setAddLoading] = useState(false);
  const [addFileList, setAddFileList] = useState([]);

  // ── Edit External Contact modal ────────────────────────────────────────────
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [editLoading, setEditLoading] = useState(false);
  const [editFileList, setEditFileList] = useState([]);
  const [editingExtId, setEditingExtId] = useState(null);

  // ── Image / Poster modals ──────────────────────────────────────────────────
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState("");
  const [posterModalVisible, setPosterModalVisible] = useState(false);
  const [currentPoster, setCurrentPoster] = useState("");

  // ── Public form link ───────────────────────────────────────────────────────
  const publicFormUrl = `${window.location.origin}/birthday-form`;

  // ── Registered columns ────────────────────────────────────────────────────
  const columns = [
    {
      title: createStyledTitle("S.No"),
      dataIndex: "key",
      key: "key",
      fixed: 'left',
      width: 55,
      render: (text) => <span style={{ fontWeight: 600, fontSize: 12, color: '#7a0000' }}>{text}</span>,
    },
    {
      title: createStyledTitle("Name"),
      dataIndex: "name",
      key: "name",
      fixed: 'left',
      width: 220,
    },
    {
      title: createStyledTitle("Phone"),
      dataIndex: "phone_no",
      key: "phone_no",
      width: 130,
    },
    {
      title: createStyledTitle("Birthday"),
      dataIndex: "dob_date",
      key: "dob_date",
      width: 100,
    },
    {
      title: createStyledTitle("DOB"),
      dataIndex: "dob",
      key: "dob",
      width: 110,
    },
    {
      title: createStyledTitle("Action"),
      key: "action",
      dataIndex: "action",
      fixed: 'right',
      width: 240,
    },
  ];

  // ── External Contacts columns ──────────────────────────────────────────────
  const extColumns = [
    {
      title: createStyledTitle("S.No"),
      dataIndex: "key",
      key: "key",
      width: 55,
      render: (text) => <span style={{ fontWeight: 600, fontSize: 12, color: '#7a0000' }}>{text}</span>,
    },
    {
      title: createStyledTitle("Name"),
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: createStyledTitle("Phone"),
      dataIndex: "phone_no",
      key: "phone_no",
      width: 130,
    },
    {
      title: createStyledTitle("Birthday"),
      dataIndex: "dob_date",
      key: "dob_date",
      width: 100,
    },
    {
      title: createStyledTitle("Village"),
      dataIndex: "village",
      key: "village",
      width: 130,
    },
    {
      title: createStyledTitle("District"),
      dataIndex: "district",
      key: "district",
      width: 120,
    },
    {
      title: createStyledTitle("Source"),
      dataIndex: "source",
      key: "source",
      width: 100,
    },
    {
      title: createStyledTitle("Action"),
      key: "action",
      dataIndex: "action",
      fixed: 'right',
      width: 260,
    },
  ];

  // ── Debounce helper ────────────────────────────────────────────────────────
  const debounce = (func, delay) => {
    let debounceTimer;
    return function () {
      const context = this;
      const args = arguments;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => func.apply(context, args), delay);
    };
  };

  // ── Registered list fetch ──────────────────────────────────────────────────
  async function getUserList(page = 1, lim = 20, sf = null, so = null, srch = '', filt = {}) {
    try {
      setCurrentPage(page);
      setLimit(lim);
      const sortParam = sf && so ? `&sortField=${sf}&sortOrder=${so}` : '';
      const searchParam = srch ? `&search=${srch}` : '';
      const filterParams = Object.keys(filt).map(key => `${key}=${filt[key]}`).join('&');
      const response = await axios.get(`${baseUrl}/users/birthdayList?page=${page}&limit=${lim}${sortParam}${searchParam}&${filterParams}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
      });
      if (response.data.status) {
        setUserList(response.data.result);
        setPagination({ current: page, pageSize: lim, total: response.data.totalCount });
      } else {
        notification.info({ message: 'Info', description: response.data.message, placement: 'topRight' });
      }
    } catch (error) {
      notification.info({ message: 'Info', description: error.response?.data?.message, placement: 'topRight' });
    }
  }

  const debouncedGetUserList = useCallback(debounce((page, lim, sf, so, srch, filt) => {
    getUserList(page, lim, sf, so, srch, filt);
  }, 300), []);

  useEffect(() => { getUserList(); }, []);

  useEffect(() => {
    const filterKeys = Object.keys(filters);
    const anyFilterHasMinLength = filterKeys.some(key => key !== 'createdBy' && filters[key].length >= 3);
    const allFiltersEmptyOrCreatedBy = filterKeys.every(key => key === 'createdBy' || !filters[key]);
    if (anyFilterHasMinLength || allFiltersEmptyOrCreatedBy) {
      debouncedGetUserList(currentPage, limit, sortField, sortOrder, search, filters);
    }
  }, [filters, currentPage, limit, sortField, sortOrder, search]);

  // ── External list fetch ────────────────────────────────────────────────────
  async function getExtList(page = 1, lim = 20, filt = {}) {
    try {
      setExtLoading(true);
      setExtPage(page);
      setExtLimit(lim);
      const filterParams = Object.keys(filt).map(key => `${key}=${filt[key]}`).join('&');
      const response = await axios.get(`${baseUrl}/external-contacts/list?page=${page}&limit=${lim}&${filterParams}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
      });
      if (response.data.status) {
        setExtList(response.data.result);
        setExtPagination({ current: page, pageSize: lim, total: response.data.totalCount });
      } else {
        notification.info({ message: 'Info', description: response.data.message, placement: 'topRight' });
      }
    } catch (error) {
      notification.info({ message: 'Info', description: error.response?.data?.message, placement: 'topRight' });
    } finally {
      setExtLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab === 'external') getExtList(1, extLimit, extFilters);
  }, [activeTab]);

  // ── Delete External Contact ────────────────────────────────────────────────
  const deleteExtContact = async (id) => {
    try {
      const response = await axios.delete(`${baseUrl}/external-contacts/delete/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
      });
      if (response.data.status) {
        notification.success({ message: 'Deleted', description: 'Contact removed successfully', placement: 'topRight' });
        getExtList(extPage, extLimit, extFilters);
      } else {
        notification.error({ message: 'Error', description: response.data.message, placement: 'topRight' });
      }
    } catch (error) {
      notification.error({ message: 'Error', description: error.response?.data?.message, placement: 'topRight' });
    }
  };

  // ── Add Contact submit ─────────────────────────────────────────────────────
  const handleAddContact = async (values) => {
    try {
      setAddLoading(true);
      const formData = new FormData();
      Object.keys(values).forEach(key => {
        if (values[key] !== undefined && values[key] !== null) {
          if (key === 'dob') {
            formData.append(key, values[key].format('YYYY-MM-DD'));
          } else {
            formData.append(key, values[key]);
          }
        }
      });
      if (addFileList.length > 0 && addFileList[0].originFileObj) {
        formData.append('profile_img', addFileList[0].originFileObj);
      }
      const response = await axios.post(`${baseUrl}/external-contacts/add`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response.data.status) {
        notification.success({ message: 'Success', description: 'Contact added successfully', placement: 'topRight' });
        setAddModalVisible(false);
        addForm.resetFields();
        setAddFileList([]);
        getExtList(1, extLimit, extFilters);
      } else {
        notification.error({ message: 'Error', description: response.data.message, placement: 'topRight' });
      }
    } catch (error) {
      notification.error({ message: 'Error', description: error.response?.data?.message, placement: 'topRight' });
    } finally {
      setAddLoading(false);
    }
  };

  // ── Edit Contact open ──────────────────────────────────────────────────────
  const openEditContact = (contact) => {
    setEditingExtId(contact.id);
    editForm.setFieldsValue({
      name: contact.name,
      relation: contact.relation || null,
      father_name: contact.father_name,
      mother_name: contact.mother_name,
      phone_no: contact.phone_no,
      dob: contact.dob ? moment(contact.dob) : null,
      village: contact.village,
      district: contact.district,
    });
    setEditFileList([]);
    setEditModalVisible(true);
  };

  // ── Edit Contact submit ────────────────────────────────────────────────────
  const handleEditContact = async (values) => {
    try {
      setEditLoading(true);
      const formData = new FormData();
      Object.keys(values).forEach(key => {
        if (values[key] !== undefined && values[key] !== null) {
          if (key === 'dob') {
            formData.append(key, values[key].format('YYYY-MM-DD'));
          } else {
            formData.append(key, values[key]);
          }
        }
      });
      if (editFileList.length > 0 && editFileList[0].originFileObj) {
        formData.append('profile_img', editFileList[0].originFileObj);
      }
      const response = await axios.put(`${baseUrl}/external-contacts/update/${editingExtId}`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response.data.status) {
        notification.success({ message: 'Success', description: 'Contact updated successfully', placement: 'topRight' });
        setEditModalVisible(false);
        editForm.resetFields();
        setEditFileList([]);
        setEditingExtId(null);
        getExtList(extPage, extLimit, extFilters);
      } else {
        notification.error({ message: 'Error', description: response.data.message, placement: 'topRight' });
      }
    } catch (error) {
      notification.error({ message: 'Error', description: error.response?.data?.message, placement: 'topRight' });
    } finally {
      setEditLoading(false);
    }
  };

  // ── Registered table handlers ──────────────────────────────────────────────
  const handleTableChange = (pag, filt, sorter) => {
    const { current, pageSize } = pag;
    const { field, order } = sorter;
    const formattedOrder = order === 'ascend' ? 'asc' : 'desc';
    setSortField(field);
    setSortOrder(formattedOrder);
    getUserList(current, pageSize, field, formattedOrder, search, filters);
  };

  const handleLimitChange = value => {
    setLimit(value);
    getUserList(1, value, sortField, sortOrder, search, filters);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // ── External table handlers ────────────────────────────────────────────────
  const handleExtTableChange = (pag) => {
    getExtList(pag.current, pag.pageSize, extFilters);
  };

  const handleExtFilterChange = (field, value) => {
    setExtFilters(prev => ({ ...prev, [field]: value }));
  };

  // ── Excel export ───────────────────────────────────────────────────────────
  const exportToExcel = async () => {
    setLoading(true);
    const sortParam = sortField && sortOrder ? `&sortField=${sortField}&sortOrder=${sortOrder}` : '';
    const searchParam = search ? `&search=${search}` : '';
    const filterParams = Object.keys(filters).map(key => `${key}=${filters[key]}`).join('&');
    const response = await axios.get(`${baseUrl}/users/birthdayList?page=1&limit=all${sortParam}${searchParam}&${filterParams}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
    });
    if (response.data.status) {
      setLoading(false);
      const formattedData = response.data.result.map((user, index) => ({
        '#': index + 1,
        'MEMBER NAME': user.name,
        'PHONE NO': user.phone_no,
        'FATHER NAME': user.father_name,
        'DOB': user.dob ? moment(user.dob).format('DD/MM/YYYY') : '-',
        'Response': user.dob_response,
      }));
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "BirthdayList");
      XLSX.writeFile(workbook, "BirthdayList.xlsx");
    } else {
      setLoading(false);
      notification.info({ message: 'Info', description: response.data.message, placement: 'topRight' });
    }
  };

  // ── Poster / Send helpers ──────────────────────────────────────────────────
  const fetchPosterBlob = async (userId, isExt = false) => {
    const url = isExt
      ? `${baseUrl}/sendMessage/birthdayPosterPreviewExt?ext_id=${userId}`
      : `${baseUrl}/sendMessage/birthdayPosterPreview?user_id=${userId}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
    });
    if (!res.ok) throw new Error('Failed to generate poster');
    return await res.blob();
  };

  const downloadBirthdayPoster = async (userId, userName, isExt = false) => {
    try {
      const blob = await fetchPosterBlob(userId, isExt);
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `birthday_poster_${userName || userId}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      notification.error({ message: 'Error', description: 'Could not generate poster. Please check birthday date is set.', placement: 'topRight' });
    }
  };

  const sendMessage = async (userId) => {
    const response = await axios.post(
      `${baseUrl}/sendMessage/birthdateSMS`,
      { user_id: userId },
      { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
    );
    if (response.data.status === true) {
      notification.success({ message: 'Success', description: response.data.message, placement: 'topRight' });
    } else {
      notification.info({ message: 'Info', description: response.data.message, placement: 'topRight' });
    }
  };

  const showConfirm = (userId) => {
    Modal.confirm({
      title: 'Are you sure you want to send birthday message Now?',
      onOk() { sendMessage(userId); },
      onCancel() {},
    });
  };

  const sendExtMessage = async (ecId) => {
    try {
      const response = await axios.post(
        `${baseUrl}/sendMessage/extBirthdaySMS`,
        { ext_id: ecId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
      );
      if (response.data.status === true) {
        notification.success({ message: 'Success', description: response.data.message, placement: 'topRight' });
      } else {
        notification.info({ message: 'Info', description: response.data.message, placement: 'topRight' });
      }
    } catch (error) {
      notification.error({ message: 'Error', description: error.response?.data?.message, placement: 'topRight' });
    }
  };

  const showExtConfirm = (ec) => {
    Modal.confirm({
      title: `${ec.name} को Birthday Poster भेजें?`,
      content: `WhatsApp: ${ec.phone_no}`,
      onOk() { sendExtMessage(ec.id); },
      onCancel() {},
    });
  };

  const viewPoster = async (userId, isExt = false) => {
    try {
      const blob = await fetchPosterBlob(userId, isExt);
      const blobUrl = URL.createObjectURL(blob);
      // Revoke previous blob URL if any
      if (currentPoster && currentPoster.startsWith('blob:')) URL.revokeObjectURL(currentPoster);
      setCurrentPoster(blobUrl);
      setPosterModalVisible(true);
    } catch {
      notification.error({ message: 'Error', description: 'Could not generate poster. Please check birthday date is set.', placement: 'topRight' });
    }
  };

  const showModal = (image) => {
    setCurrentImage(image || userDummy);
    setIsModalVisible(true);
  };

  const handleCancel = () => setIsModalVisible(false);

  // ── Upload helpers ─────────────────────────────────────────────────────────
  const beforeUpload = () => false; // prevent auto-upload

  // ── Contact form fields (shared between add/edit) ─────────────────────────
  const ContactFormFields = ({ fileList, onFileChange }) => (
    <>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="name" label="नाम (Name)" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="पूरा नाम" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="relation" label="संबंध (Relation)">
            <Select placeholder="Select Relation" allowClear>
              <Option value="सुपुत्र श्री">Son of Mr. (सुपुत्र श्री)</Option>
              <Option value="सुपुत्री श्री">Daughter of Mr. (सुपुत्री श्री)</Option>
              <Option value="पत्नी श्री">Wife of Mr. (पत्नी श्री)</Option>
              <Option value="गोद पुत्र श्री">Adopted Son of Mr. (गोद पुत्र श्री)</Option>
              <Option value="शिष्य श्री">Disciple of Mr. (शिष्य श्री)</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="father_name" label="पिता का नाम (Father Name)">
            <Input placeholder="पिता का नाम" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="mother_name" label="माता का नाम (Mother Name)">
            <Input placeholder="माता का नाम" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="phone_no" label="WhatsApp / Phone" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="Mobile number" maxLength={10} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="dob" label="जन्म तिथि (DOB)" rules={[{ required: true, message: 'Required' }]}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="DD/MM/YYYY" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="village" label="गाँव (Village)">
            <Input placeholder="गाँव का नाम" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="district" label="जिला (District)">
            <Input placeholder="जिले का नाम" />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item label="Photo (optional)">
        <Upload
          listType="picture"
          fileList={fileList}
          beforeUpload={beforeUpload}
          onChange={({ fileList: fl }) => onFileChange(fl.slice(-1))}
          accept=".jpg,.jpeg,.png,.webp"
        >
          <Button icon={<UploadOutlined />}>Select Photo</Button>
        </Upload>
      </Form.Item>
    </>
  );

  // ── Registered Members tab content ────────────────────────────────────────
  const registeredTabContent = (
    <Table
      columns={columns}
      scroll={{ x: 'max-content' }}
      bordered
      title={() => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Row gutter={[16, 16]} style={{ width: '100%' }}>
            <Col span={6}>
              <Input placeholder="Phone" value={filters.phone} onChange={e => handleFilterChange('phone', e.target.value)} />
            </Col>
            <Col span={6}>
              <Input placeholder="Name" value={filters.name} onChange={e => handleFilterChange('name', e.target.value)} />
            </Col>
            <Col span={4}>
              <DatePicker
                className="filter_dob CustomInputSet"
                format="DD/MM"
                value={filters.dob ? moment(filters.dob, 'DD/MM') : null}
                onChange={(date, dateString) => handleFilterChange('dob', dateString)}
                placeholder="DOB (DD/MM)"
              />
            </Col>
            <Col span={8}>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  icon={<SearchOutlined />}
                  onClick={() => getUserList(1, limit, sortField, sortOrder, search, filters)}
                  style={{ background: '#7a0000', color: '#fff', borderColor: '#7a0000' }}
                >
                  Search
                </Button>
                {hasPermission('/reports/birthday-list', 'downloadPermission') && (
                  <Button icon={<FileExcelOutlined />} onClick={exportToExcel}>
                    Excel
                  </Button>
                )}
              </div>
            </Col>
            <Divider style={{ margin: '8px 0' }} />
            <Col xs={24}>
              <span style={{ marginRight: 10, fontSize: 13 }}>Records per page:</span>
              <Select defaultValue={20} onChange={handleLimitChange}>
                <Option value={20}>20</Option>
                <Option value={40}>40</Option>
                <Option value={60}>60</Option>
                <Option value={80}>80</Option>
                <Option value={100}>100</Option>
              </Select>
            </Col>
          </Row>
        </div>
      )}
      dataSource={userList.map((user, index) => ({
        key: (limit * (currentPage - 1)) + index + 1,
        name: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar
              shape="square"
              size={34}
              src={user.profile_img || userDummy}
              icon={!user.profile_img && <UserOutlined />}
              onClick={() => showModal(user.profile_img || userDummy)}
              style={{ cursor: 'pointer', borderRadius: 4, flexShrink: 0 }}
            />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#7a0000' }}>
                <Link to={`/users/view/${user.id}`} style={{ color: '#1890ff' }}>{user.name}</Link>
              </div>
              {(user.relation_hn || user.father_name) && (
                <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                  {user.relation_hn && <span style={{ color: '#aaa' }}>{user.relation_hn} </span>}
                  {user.father_name}
                </div>
              )}
            </div>
          </div>
        ),
        phone_no: (
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>{user.phone_no || '—'}</div>
            {user.alternate_phone_no && <div style={{ fontSize: 11, color: '#888' }}>{user.alternate_phone_no}</div>}
          </div>
        ),
        dob_date: <span style={{ fontSize: 13, fontWeight: 500 }}>{user.dob ? moment(user.dob).format('DD MMM') : '—'}</span>,
        dob: <span style={{ fontSize: 12 }}>{user.dob ? moment(user.dob).format('DD/MM/YYYY') : '—'}</span>,
        action: (
          <div style={{ display: 'flex', flexDirection: 'row', gap: 6 }}>
            <Button
              size="small"
              icon={<SendOutlined />}
              onClick={() => showConfirm(user.id)}
              style={{ background: '#7a0000', color: '#fff', borderColor: '#7a0000' }}
            >
              Send
            </Button>
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => viewPoster(user.id)}
              style={{ background: '#1890ff', borderColor: '#1890ff', color: '#fff' }}
            >
              View
            </Button>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => downloadBirthdayPoster(user.id, user.name)}
              style={{ background: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
            >
              Poster
            </Button>
          </div>
        ),
      }))}
      pagination={{
        ...pagination,
        showTotal: total => `Total ${total} records`,
        style: { marginRight: 20 }
      }}
      onChange={handleTableChange}
      className="ant-border-space"
    />
  );

  // ── External Contacts tab content ─────────────────────────────────────────
  const externalTabContent = (
    <>
      {/* Toolbar */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <Row gutter={[12, 12]} align="middle">
          <Col span={5}>
            <Input
              placeholder="Search Name"
              value={extFilters.name}
              onChange={e => handleExtFilterChange('name', e.target.value)}
            />
          </Col>
          <Col span={5}>
            <Input
              placeholder="Search Phone"
              value={extFilters.phone}
              onChange={e => handleExtFilterChange('phone', e.target.value)}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Source"
              style={{ width: '100%' }}
              allowClear
              value={extFilters.source || undefined}
              onChange={val => handleExtFilterChange('source', val || '')}
            >
              <Option value="internal">Internal</Option>
              <Option value="external">External (Form)</Option>
            </Select>
          </Col>
          <Col>
            <Button
              icon={<SearchOutlined />}
              onClick={() => getExtList(1, extLimit, extFilters)}
              style={{ background: '#7a0000', color: '#fff', borderColor: '#7a0000' }}
            >
              Search
            </Button>
          </Col>
          <Col flex="auto" style={{ textAlign: 'right' }}>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              {/* Public form link */}
              <Button
                icon={<LinkOutlined />}
                onClick={() => {
                  navigator.clipboard.writeText(publicFormUrl);
                  notification.success({ message: 'Link Copied!', description: publicFormUrl, placement: 'topRight' });
                }}
              >
                Copy Form Link
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ background: '#7a0000', borderColor: '#7a0000' }}
                onClick={() => { addForm.resetFields(); setAddFileList([]); setAddModalVisible(true); }}
              >
                Add Contact
              </Button>
            </div>
          </Col>
        </Row>
        {/* Show the public link */}
        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
          <span style={{ marginRight: 6 }}>Public Form Link:</span>
          <a href={publicFormUrl} target="_blank" rel="noreferrer" style={{ color: '#1890ff', wordBreak: 'break-all' }}>
            {publicFormUrl}
          </a>
        </div>
      </div>

      {/* Table */}
      <Table
        columns={extColumns}
        scroll={{ x: 'max-content' }}
        bordered
        loading={extLoading}
        dataSource={extList.map((ec, index) => ({
          key: (extLimit * (extPage - 1)) + index + 1,
          name: (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar
                shape="square"
                size={34}
                src={ec.profile_img || userDummy}
                icon={!ec.profile_img && <UserOutlined />}
                onClick={() => showModal(ec.profile_img || userDummy)}
                style={{ cursor: 'pointer', borderRadius: 4, flexShrink: 0 }}
              />
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#7a0000' }}>{ec.name}</div>
                {ec.father_name && <div style={{ fontSize: 11, color: '#666' }}>{ec.father_name}</div>}
              </div>
            </div>
          ),
          phone_no: <span style={{ fontWeight: 500, fontSize: 13 }}>{ec.phone_no || '—'}</span>,
          dob_date: (
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{ec.dob ? moment(ec.dob).format('DD MMM') : '—'}</div>
              <div style={{ fontSize: 11, color: '#888' }}>{ec.dob ? moment(ec.dob).format('DD/MM/YYYY') : ''}</div>
            </div>
          ),
          village: <span style={{ fontSize: 13 }}>{ec.village || '—'}</span>,
          district: <span style={{ fontSize: 13 }}>{ec.district || '—'}</span>,
          source: (
            <Tag color={ec.source === 'external' ? 'blue' : 'green'} style={{ fontSize: 11 }}>
              {ec.source === 'external' ? 'Form' : 'Admin'}
            </Tag>
          ),
          action: (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              <Button
                size="small"
                icon={<SendOutlined />}
                onClick={() => showExtConfirm(ec)}
                style={{ background: '#7a0000', color: '#fff', borderColor: '#7a0000' }}
              >
                Send Poster
              </Button>
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => viewPoster(ec.id, true)}
                style={{ background: '#1890ff', borderColor: '#1890ff', color: '#fff' }}
              >
                View
              </Button>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => downloadBirthdayPoster(ec.id, ec.name, true)}
                style={{ background: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
              >
                Poster
              </Button>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditContact(ec)}
                style={{ background: '#faad14', borderColor: '#faad14', color: '#fff' }}
              >
                Edit
              </Button>
              <Popconfirm
                title={`"${ec.name}" को हटाएं?`}
                description="यह संपर्क स्थायी रूप से हटा दिया जाएगा।"
                onConfirm={() => deleteExtContact(ec.id)}
                okText="हाँ, हटाएं"
                cancelText="रद्द करें"
                okButtonProps={{ danger: true }}
              >
                <Button
                  size="small"
                  icon={<DeleteOutlined />}
                  danger
                >
                  Delete
                </Button>
              </Popconfirm>
            </div>
          ),
        }))}
        pagination={{
          ...extPagination,
          showTotal: total => `Total ${total} records`,
          style: { marginRight: 20 }
        }}
        onChange={handleExtTableChange}
        className="ant-border-space"
      />
    </>
  );

  return (
    <>
      {loading && (
        <div className="full-page-loader">
          <Spin size="large" />
        </div>
      )}

      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0'}}
              title={
                <Tabs
                  activeKey={activeTab}
                  onChange={(tab) => {
                    setActiveTab(tab);
                  }}
                  tabBarStyle={{ margin: 0, paddingLeft: 16 }}
                  tabBarGutter={32}
                >
                  <TabPane
                    tab={
                      <span style={{ color: activeTab === 'registered' ? '#faad14' : '#bbb', fontWeight: 600, fontSize: 13 }}>
                        Registered Members
                      </span>
                    }
                    key="registered"
                  />
                  <TabPane
                    tab={
                      <span style={{ color: activeTab === 'external' ? '#40a9ff' : '#bbb', fontWeight: 600, fontSize: 13 }}>
                        External Contacts
                      </span>
                    }
                    key="external"
                  />
                </Tabs>
              }
            >
              <div className="table-responsive">
                {activeTab === 'registered' ? registeredTabContent : externalTabContent}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Profile image modal */}
        <Modal open={isModalVisible} footer={null} onCancel={handleCancel}>
          <img src={currentImage} alt="Profile" style={{ width: '100%' }} />
        </Modal>

        {/* Poster preview modal */}
        <Modal
          open={posterModalVisible}
          footer={null}
          onCancel={() => { if (currentPoster && currentPoster.startsWith('blob:')) URL.revokeObjectURL(currentPoster); setPosterModalVisible(false); setCurrentPoster(''); }}
          title={<span style={{ color: '#7a0000', fontWeight: 700 }}>Birthday Poster</span>}
          width={480}
        >
          <img src={currentPoster} alt="Birthday Poster" style={{ width: '100%', borderRadius: 6 }} />
        </Modal>

        {/* Add Contact modal */}
        <Modal
          open={addModalVisible}
          onCancel={() => { setAddModalVisible(false); addForm.resetFields(); setAddFileList([]); }}
          title={<span style={{ color: '#7a0000', fontWeight: 700 }}>Add External Contact</span>}
          footer={null}
          width={600}
          destroyOnClose
        >
          <Form form={addForm} layout="vertical" onFinish={handleAddContact} style={{ marginTop: 16 }}>
            <ContactFormFields fileList={addFileList} onFileChange={setAddFileList} />
            <div style={{ textAlign: 'right', marginTop: 8 }}>
              <Button style={{ marginRight: 8 }} onClick={() => { setAddModalVisible(false); addForm.resetFields(); setAddFileList([]); }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={addLoading} style={{ background: '#7a0000', borderColor: '#7a0000' }}>
                Add Contact
              </Button>
            </div>
          </Form>
        </Modal>

        {/* Edit Contact modal */}
        <Modal
          open={editModalVisible}
          onCancel={() => { setEditModalVisible(false); editForm.resetFields(); setEditFileList([]); setEditingExtId(null); }}
          title={<span style={{ color: '#7a0000', fontWeight: 700 }}>Edit External Contact</span>}
          footer={null}
          width={600}
          destroyOnClose
        >
          <Form form={editForm} layout="vertical" onFinish={handleEditContact} style={{ marginTop: 16 }}>
            <ContactFormFields fileList={editFileList} onFileChange={setEditFileList} />
            <div style={{ textAlign: 'right', marginTop: 8 }}>
              <Button style={{ marginRight: 8 }} onClick={() => { setEditModalVisible(false); editForm.resetFields(); setEditFileList([]); setEditingExtId(null); }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={editLoading} style={{ background: '#7a0000', borderColor: '#7a0000' }}>
                Save Changes
              </Button>
            </div>
          </Form>
        </Modal>
      </div>
    </>
  );
}

export default BirthdayReport;
